import React, { useRef } from "react";

import { mdiMenu, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import AdbreakRuleIcon from "./icons/AdbreakRuleIcon";
import RandomRuleIcon from "./icons/RandomRuleIcon";
import SequentialRuleIcon from "./icons/SequentialRuleIcon";
import SpecificRuleIcon from "./icons/SpecificRuleIcon";
import { TIMESLOT_RULES_COLUMN_CLASSNAMES } from "./TimeslotRuleHeader";
import { TimeslotRuleMode } from "../../../api/FastAPI.types";
import { TimeslotColumns } from "../../../types/fast";

import type { JSX } from "react";
import type { ConnectDragSource } from "react-dnd";

interface TimeslotItemProps {
  columns: {
    [TimeslotColumns.Rank]: number;
    [TimeslotColumns.Mode]: TimeslotRuleMode;
    [TimeslotColumns.Source]: JSX.Element;
    [TimeslotColumns.Count]: JSX.Element;
    [TimeslotColumns.StartIndex]?: JSX.Element;
    [TimeslotColumns.Increment]?: JSX.Element;
    [TimeslotColumns.Ads]?: JSX.Element;
  };
  onClickDelete: (uuid: string) => void;
  isDragging: boolean;
  isDragDisabled: boolean;
  dragRef: ConnectDragSource;
  uuid: string;
}

export default function TimeslotRuleItem({
  columns,
  onClickDelete,
  isDragging,
  dragRef: dragConnector,
  isDragDisabled,
  uuid,
}: TimeslotItemProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const dragRef = useRef<HTMLDivElement>(null);

  dragConnector(dragRef);

  const getModeIcon = (): JSX.Element | null => {
    switch (columns[TimeslotColumns.Mode]) {
      case TimeslotRuleMode.Random:
        return <RandomRuleIcon />;
      case TimeslotRuleMode.Sequential:
        return <SequentialRuleIcon />;
      case TimeslotRuleMode.Specific:
        return <SpecificRuleIcon />;
      case TimeslotRuleMode.Adbreak:
        return <AdbreakRuleIcon />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-card flex w-full divide-x font-medium">
      <div
        ref={dragRef}
        className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Rank]}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
        }}
      >
        <Icon color="#98A2B3" path={mdiMenu} size={1.25} />
        <div className="ml-[0.46875rem]">
          {!isDragDisabled && `${columns[TimeslotColumns.Rank] + 1}`}
        </div>
      </div>
      <div
        className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Mode]}
        data-tooltip-id={APP_TOOLTIP_ID}
        data-tooltip-offset={-5}
        data-tooltip-content={t(
          `timeslotRuleMode.${columns[TimeslotColumns.Mode]}`,
        )}
      >
        {getModeIcon()}
      </div>
      <div className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Source]}>
        {columns[TimeslotColumns.Source]}
      </div>
      <div className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Count]}>
        {columns[TimeslotColumns.Count]}
      </div>
      <div
        className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.StartIndex]}
      >
        {columns[TimeslotColumns.StartIndex]}
      </div>
      <div
        className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Increment]}
      >
        {columns[TimeslotColumns.Increment]}
      </div>
      <div className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Ads]}>
        {columns[TimeslotColumns.Ads]}
      </div>
      <div className={TIMESLOT_RULES_COLUMN_CLASSNAMES[TimeslotColumns.Delete]}>
        <div
          className="cursor-pointer"
          onClick={(): void => onClickDelete(uuid)}
        >
          <Icon color="#D92D20" path={mdiTrashCanOutline} size={1.25} />
        </div>
      </div>
    </div>
  );
}
